import { createElement } from "./utils.js";

const SECTION_DEFINITIONS = [
  {
    key: "readability",
    title: "Readability",
    controls: [
      { type: "spinner", action: "contentScaling" },
      { type: "spinner", action: "fontSizing" },
      { type: "spinner", action: "lineHeight" },
      { type: "spinner", action: "letterSpacing" },
      { type: "toggle", action: "readableFont" },
      { type: "toggle", action: "dyslexiaFont" },
      { type: "toggle", action: "alignLeft" },
      { type: "toggle", action: "alignCenter" },
      { type: "toggle", action: "alignRight" },
    ],
  },
  {
    key: "visualAids",
    title: "Visual Aids",
    controls: [
      { type: "toggle", action: "darkContrast" },
      { type: "toggle", action: "lightContrast" },
      { type: "toggle", action: "monochrome" },
      { type: "toggle", action: "highSaturation" },
      { type: "toggle", action: "highContrast" },
      { type: "toggle", action: "lowSaturation" },
      { type: "toggle", action: "highlightTitles" },
      { type: "toggle", action: "highlightLinks" },
      { type: "toggle", action: "highlightHover" },
      { type: "toggle", action: "highlightFocus" },
    ],
  },
  {
    key: "navigationTools",
    title: "Navigation & Tools",
    controls: [
      { type: "toggle", action: "hideImages" },
      { type: "toggle", action: "hideEmoji" },
      { type: "toggle", action: "readingMask" },
      { type: "toggle", action: "readingGuide" },
      { type: "toggle", action: "bigBlackCursor" },
      { type: "toggle", action: "bigWhiteCursor" },
    ],
  },
];

const VALUE_UNITS = {
  contentScaling: "%",
  fontSizing: "px",
  lineHeight: "%",
  letterSpacing: "px",
};

const VALUE_STEP = {
  contentScaling: 5,
  fontSizing: 1,
  lineHeight: 5,
  letterSpacing: 1,
};

export function createUI(config, handlers) {
  const controls = new Map();
  const spinners = new Map();
  const spinnerButtons = new Map();
  const controlLabels = new Map();
  const sectionTitleNodes = [];
  const info = {
    open: false,
    root: null,
    trigger: null,
    overlay: null,
    panel: null,
    customTriggerBindings: [],
    headerTitle: null,
    headerSubtitle: null,
    headerCloseButton: null,
    footerResetButton: null,
    footerCloseButton: null,
    languageButtons: new Map(),
    currentLanguage: null,
  };
  const localization = config.localization || {};
  const localeMap = localization.locales || {};
  const availableLanguages = Array.isArray(localization.languages) && localization.languages.length
    ? localization.languages
    : Object.keys(localeMap);
  const defaultLanguage =
    localization.defaultLanguage ||
    config.defaultLanguage ||
    config.language ||
    availableLanguages[0] ||
    "en";
  const flags = localization.flags || {};
  const showLanguageCodes = Boolean(config.languageSelector?.showCodes);

  function mount() {
    const root = createElement("div", {
      id: "rl-root",
      className: positionClass(config.buttonPosition),
    });
    const fallbackOffset = normalizeCssLength(config.buttonMargin, "20px");
    root.style.setProperty("--rl-trigger-margin", fallbackOffset);
    root.style.setProperty(
      "--rl-trigger-offset-x",
      normalizeCssLength(config.buttonOffsetX, fallbackOffset),
    );
    root.style.setProperty(
      "--rl-trigger-offset-y",
      normalizeCssLength(config.buttonOffsetY, fallbackOffset),
    );

    const overlay = createElement("div", { id: "rl-overlay" });
    overlay.addEventListener("click", () => {
      if (config.closeOnOverlay) {
        close();
      }
    });
    root.appendChild(overlay);

    const panel = createElement("section", { id: "rl-panel", role: "dialog", "aria-modal": "true" });
    panel.appendChild(buildHeader());
    panel.appendChild(buildContent());
    panel.appendChild(buildFooter());
    root.appendChild(panel);

    let trigger = null;
    if (config.showButton) {
      trigger = createElement("button", {
        id: "rl-trigger",
        type: "button",
        className: "rl-trigger-button",
        "aria-label":
          config.buttonAriaLabel || getText("aria.togglePanel", "Toggle ReadlyGo panel"),
      });
      renderTriggerContent(trigger, {
        variant: config.buttonVariant,
        iconUrl: config.buttonIconUrl,
        iconSize: config.buttonIconSize,
        label: config.buttonLabel,
        title: config.buttonTitle || getText("aria.togglePanel", "Toggle ReadlyGo panel"),
      });
      applyTriggerVisualStyles(trigger, {
        background: config.buttonBackground,
        textColor: config.buttonTextColor,
        iconColor: config.buttonIconColor,
        borderColor: config.buttonBorderColor,
        boxShadow: config.buttonBoxShadow,
        boxShadowHover: config.buttonBoxShadowHover,
        padding: config.buttonPadding,
      });
      trigger.addEventListener("click", toggleOpen);
      root.appendChild(trigger);
    }

    document.body.appendChild(root);

    info.root = root;
    info.overlay = overlay;
    info.panel = panel;
    info.trigger = trigger;

    mountCustomTriggers();
    setLanguage(config.language || defaultLanguage, { persist: false });
  }

  function buildHeader() {
    const header = createElement("header", { id: "rl-header" });
    const headerMain = createElement("div", { id: "rl-header-main" });
    const title = createElement("h2", { id: "rl-title" }, config.popupTitle);
    const subtitle = createElement("p", { id: "rl-subtitle" }, config.panelSubtitle || "");
    const tools = createElement("div", { id: "rl-header-tools" });
    const closeButton = createElement("button", {
      id: "rl-close",
      type: "button",
      "aria-label": getText("aria.closePanel", "Close ReadlyGo panel"),
    });
    closeButton.textContent = "×";
    closeButton.addEventListener("click", close);
    headerMain.appendChild(title);
    if (config.panelSubtitle) {
      headerMain.appendChild(subtitle);
    }

    if (config.languageSelector?.enabled !== false && availableLanguages.length > 1) {
      tools.appendChild(buildLanguageSelector());
    }
    tools.appendChild(closeButton);

    header.appendChild(headerMain);
    header.appendChild(tools);

    info.headerTitle = title;
    info.headerSubtitle = subtitle;
    info.headerCloseButton = closeButton;
    return header;
  }

  function buildLanguageSelector() {
    const size = Number(config.languageSelector?.size) || 18;
    const wrap = createElement("div", {
      id: "rl-language-switcher",
      role: "group",
      "aria-label": getText("aria.languageSelector", "Select language"),
    });

    availableLanguages.forEach((languageCode) => {
      const button = createElement("button", {
        type: "button",
        className: "rl-lang-btn",
        "data-language": languageCode,
        "aria-label": languageCode,
        "aria-pressed": "false",
      });
      button.style.setProperty("--rl-lang-flag-size", `${size}px`);

      const flag = flags[languageCode];
      if (flag) {
        const image = createElement("img", {
          src: flag,
          alt: languageCode,
          loading: "lazy",
          decoding: "async",
        });
        button.appendChild(image);
      }
      if (showLanguageCodes || !flag) {
        button.appendChild(
          createElement("span", { className: "rl-lang-code" }, String(languageCode).toUpperCase()),
        );
      }

      button.addEventListener("click", () => {
        setLanguage(languageCode);
      });

      info.languageButtons.set(languageCode, button);
      wrap.appendChild(button);
    });

    return wrap;
  }

  function buildContent() {
    const content = createElement("div", { id: "rl-content" });

    for (const sectionDef of SECTION_DEFINITIONS) {
      const visibleControls = sectionDef.controls.filter(
        (item) => config.enable[item.action],
      );
      if (visibleControls.length < 1) {
        continue;
      }

      const title = createElement(
        "h3",
        { className: "rl-section-title" },
        getSectionTitle(sectionDef),
      );
      sectionTitleNodes.push({
        key: sectionDef.key,
        defaultTitle: sectionDef.title,
        node: title,
      });
      const grid = createElement("div", { className: "rl-grid" });

      visibleControls.forEach((item) => {
        if (item.type === "toggle") {
          const button = buildToggle(item.action);
          grid.appendChild(button);
          controls.set(item.action, button);
        } else if (item.type === "spinner") {
          const spinner = buildSpinner(item.action);
          grid.appendChild(spinner.container);
          spinners.set(item.action, spinner.valueLabel);
        }
      });

      content.appendChild(title);
      content.appendChild(grid);
    }

    return content;
  }

  function buildFooter() {
    const footer = createElement("div", { id: "rl-footer" });
    const actions = createElement("div", { id: "rl-footer-actions" });

    const resetButton = createElement("button", {
      type: "button",
      id: "rl-reset",
    });
    resetButton.textContent = getText("buttons.reset", "Reset");
    resetButton.addEventListener("click", () => handlers.onReset());

    const closeButton = createElement("button", {
      type: "button",
      id: "rl-close-footer",
    });
    closeButton.textContent = getText("buttons.close", "Close");
    closeButton.addEventListener("click", close);

    actions.appendChild(resetButton);
    actions.appendChild(closeButton);
    footer.appendChild(actions);

    if (config.branding?.enabled && config.branding?.text) {
      const brandingAttrs = {
        id: "rl-branding",
      };
      let brandNode = null;
      if (config.branding.url) {
        brandNode = createElement("a", {
          ...brandingAttrs,
          href: config.branding.url,
          target: config.branding.target || "_blank",
          rel: config.branding.rel || "noopener noreferrer",
        }, config.branding.text);
      } else {
        brandNode = createElement("div", brandingAttrs, config.branding.text);
      }
      footer.appendChild(brandNode);
    }

    info.footerResetButton = resetButton;
    info.footerCloseButton = closeButton;

    return footer;
  }

  function setLanguage(nextLanguage, options = {}) {
    const language = resolveLanguage(nextLanguage);
    info.currentLanguage = language;
    applyLanguageToUi();
    if (options.persist !== false) {
      handlers.onLanguageChange?.(language);
    }
  }

  function getLanguage() {
    return info.currentLanguage || defaultLanguage;
  }

  function resolveLanguage(nextLanguage) {
    if (availableLanguages.includes(nextLanguage)) {
      return nextLanguage;
    }
    if (availableLanguages.includes(defaultLanguage)) {
      return defaultLanguage;
    }
    return availableLanguages[0] || "en";
  }

  function getLocale(language = info.currentLanguage) {
    return localeMap[resolveLanguage(language)] || {};
  }

  function getText(path, fallback = "") {
    const locale = getLocale();
    const value = readLocaleValue(locale, path);
    return typeof value === "string" ? value : fallback;
  }

  function getSectionTitle(sectionDef) {
    return getText(`sections.${sectionDef.key}`, sectionDef.title);
  }

  function getActionLabel(action) {
    const translated = getText(`actions.${action}`, "");
    if (translated) {
      return translated;
    }
    return config.labels[action] ?? action;
  }

  function getLanguageOptionAria(languageCode) {
    const normalizedCode = String(languageCode || "").trim().toLowerCase();
    if (normalizedCode === "it") {
      return getText("aria.languageOptionIt", "Switch to Italian");
    }
    if (normalizedCode === "en") {
      return getText("aria.languageOptionEn", "Switch to English");
    }
    const languageName =
      readLocaleValue(localeMap[normalizedCode], "name") ||
      String(languageCode || "").toUpperCase();
    return `${getText("aria.languageSelector", "Select language")}: ${languageName}`;
  }

  function getSpinnerAriaLabel(direction, action) {
    const actionLabel = getActionLabel(action);
    if (direction === "decrease") {
      return `${getText("aria.decrease", "Decrease")} ${actionLabel}`;
    }
    return `${getText("aria.increase", "Increase")} ${actionLabel}`;
  }

  function applyLanguageToUi() {
    const locale = getLocale();

    if (info.headerTitle) {
      info.headerTitle.textContent = getText("panelTitle", config.popupTitle);
    }
    if (info.headerSubtitle) {
      const subtitle = getText("panelSubtitle", config.panelSubtitle || "");
      info.headerSubtitle.textContent = subtitle;
      info.headerSubtitle.classList.toggle("rl-hidden", !subtitle);
    }
    if (info.headerCloseButton) {
      info.headerCloseButton.setAttribute(
        "aria-label",
        getText("aria.closePanel", "Close ReadlyGo panel"),
      );
      info.headerCloseButton.setAttribute("title", getText("buttons.close", "Close"));
    }
    if (info.footerResetButton) {
      info.footerResetButton.textContent = getText("buttons.reset", "Reset");
    }
    if (info.footerCloseButton) {
      info.footerCloseButton.textContent = getText("buttons.close", "Close");
    }
    if (info.trigger) {
      info.trigger.setAttribute(
        "aria-label",
        config.buttonAriaLabel || getText("aria.togglePanel", "Toggle ReadlyGo panel"),
      );
      if (!config.buttonTitle) {
        info.trigger.setAttribute("title", getText("aria.togglePanel", "Toggle ReadlyGo panel"));
      }
    }

    sectionTitleNodes.forEach((item) => {
      item.node.textContent = getText(`sections.${item.key}`, item.defaultTitle);
    });
    controlLabels.forEach((node, action) => {
      node.textContent = getActionLabel(action);
    });
    spinnerButtons.forEach((buttons, action) => {
      buttons.minus.setAttribute("aria-label", getSpinnerAriaLabel("decrease", action));
      buttons.plus.setAttribute("aria-label", getSpinnerAriaLabel("increase", action));
    });

    const switcher = info.root?.querySelector?.("#rl-language-switcher");
    if (switcher) {
      switcher.setAttribute("aria-label", getText("aria.languageSelector", "Select language"));
    }

    info.languageButtons.forEach((button, languageCode) => {
      const active = languageCode === getLanguage();
      button.classList.toggle("rl-active", active);
      button.setAttribute("aria-pressed", active ? "true" : "false");
      button.setAttribute("aria-label", getLanguageOptionAria(languageCode));
      button.setAttribute("title", getLanguageOptionAria(languageCode));
    });
  }

  function mountCustomTriggers() {
    const customConfig = config.customTrigger || {};
    if (!customConfig.enabled) {
      return;
    }

    const selector = customConfig.selector || "readlygo-trigger,[data-readlygo-trigger]";
    let hosts = [];
    try {
      hosts = Array.from(document.querySelectorAll(selector));
    } catch {
      return;
    }

    hosts.forEach((host) => {
      const binding = createCustomTriggerBinding(host, customConfig);
      if (binding) {
        info.customTriggerBindings.push(binding);
      }
    });
  }

  function createCustomTriggerBinding(host, customConfig) {
    if (!host) {
      return null;
    }

    const hostTag = host.tagName?.toLowerCase();
    const decorateSelf = hostTag === "button" || hostTag === "a";
    let button = decorateSelf ? host : null;
    let generated = false;

    if (!button) {
      button = Array.from(host.children || []).find(
        (child) =>
          child.tagName?.toLowerCase() === "button" &&
          child.classList.contains("rl-inline-trigger") &&
          child.dataset.generated === "true",
      );
      if (!button) {
        button = createElement("button", {
          type: "button",
          className: "rl-inline-trigger rl-trigger-button",
          "data-generated": "true",
        });
        host.appendChild(button);
        generated = true;
      }
    }

    const customOptions = resolveCustomTriggerOptions(host, customConfig);

    button.classList.add("rl-inline-trigger", "rl-trigger-button");
    button.classList.remove("rl-size-sm", "rl-size-md", "rl-size-lg");
    button.classList.add(`rl-size-${customOptions.size}`);

    if (customOptions.className) {
      customOptions.className
        .split(/\s+/)
        .filter(Boolean)
        .forEach((item) => button.classList.add(item));
    }

    applyTriggerVisualStyles(button, customOptions);

    if (hostTag === "a") {
      button.setAttribute("role", "button");
      if (!button.getAttribute("href")) {
        button.setAttribute("href", "#");
      }
    } else if (hostTag === "button") {
      if (!button.getAttribute("type")) {
        button.setAttribute("type", "button");
      }
    }

    renderTriggerContent(button, {
      variant: customOptions.variant,
      iconUrl: customOptions.iconUrl,
      iconSize: customOptions.iconSize,
      label: customOptions.label,
      ariaLabel: customOptions.ariaLabel,
      title: customOptions.title,
    });

    const onClick = (event) => {
      event.preventDefault();
      toggleOpen();
    };
    button.addEventListener("click", onClick);

    return {
      host,
      button,
      onClick,
      generated,
    };
  }

  function resolveCustomTriggerOptions(host, customConfig) {
    const dataset = host.dataset || {};
    return {
      variant: normalizeTriggerVariant(dataset.rgVariant || customConfig.variant, "icon-text"),
      size: normalizeTriggerSize(dataset.rgSize || customConfig.size, "md"),
      label: dataset.rgLabel || customConfig.label || config.buttonLabel,
      iconUrl: dataset.rgIconUrl || customConfig.iconUrl || config.buttonIconUrl,
      iconSize: dataset.rgIconSize || customConfig.iconSize || config.buttonIconSize,
      background: dataset.rgBackground || customConfig.background || "",
      textColor: dataset.rgTextColor || dataset.rgColor || customConfig.textColor || customConfig.color || "",
      iconColor: dataset.rgIconColor || customConfig.iconColor || "",
      borderColor: dataset.rgBorderColor || customConfig.borderColor || "",
      boxShadow: dataset.rgBoxShadow || customConfig.boxShadow || "",
      boxShadowHover:
        dataset.rgBoxShadowHover || dataset.rgHoverBoxShadow || customConfig.boxShadowHover || "",
      padding: dataset.rgPadding || customConfig.padding || "",
      className: dataset.rgClassName || customConfig.className || "",
      ariaLabel:
        dataset.rgAriaLabel ||
        customConfig.ariaLabel ||
        config.buttonAriaLabel ||
        customConfig.label ||
        config.buttonLabel,
      title: dataset.rgTitle || customConfig.title || "",
    };
  }

  function renderTriggerContent(button, options = {}) {
    if (!button) {
      return;
    }

    const variant = normalizeTriggerVariant(options.variant, "icon");
    const label = options.label || config.buttonLabel || "ReadlyGo";
    const iconUrl = options.iconUrl || "";
    const iconSize = normalizeIconSize(options.iconSize, config.buttonIconSize);
    const ariaLabel = options.ariaLabel || config.buttonAriaLabel || label;
    const title = options.title || label;

    button.classList.remove("rl-variant-icon", "rl-variant-text", "rl-variant-icon-text");
    button.classList.add(`rl-variant-${variant}`);
    button.setAttribute("aria-label", ariaLabel);
    button.setAttribute("title", title);

    if (iconUrl) {
      button.style.setProperty("--rl-trigger-icon-url", `url("${escapeCssUrl(iconUrl)}")`);
    } else {
      button.style.removeProperty("--rl-trigger-icon-url");
    }
    button.style.setProperty("--rl-trigger-icon-size", iconSize);

    button.textContent = "";

    if (variant !== "text" && iconUrl) {
      button.appendChild(
        createElement("span", {
          className: "rl-trigger-icon",
          "aria-hidden": "true",
        }),
      );
    }
    if (variant !== "icon" || !iconUrl) {
      button.appendChild(
        createElement("span", { className: "rl-trigger-label" }, label),
      );
    }
  }

  function applyTriggerVisualStyles(button, options = {}) {
    if (!button || !options) {
      return;
    }

    if (options.background) {
      button.style.background = String(options.background);
    } else {
      button.style.removeProperty("background");
    }

    if (options.textColor) {
      button.style.color = String(options.textColor);
    } else {
      button.style.removeProperty("color");
    }

    if (options.borderColor) {
      button.style.borderColor = String(options.borderColor);
    } else {
      button.style.removeProperty("border-color");
    }

    if (options.boxShadow) {
      button.style.setProperty("--rl-trigger-shadow", String(options.boxShadow));
    } else {
      button.style.removeProperty("--rl-trigger-shadow");
    }

    if (options.boxShadowHover) {
      button.style.setProperty("--rl-trigger-shadow-hover", String(options.boxShadowHover));
    } else {
      button.style.removeProperty("--rl-trigger-shadow-hover");
    }

    const iconColor = options.iconColor || options.textColor || "";
    if (iconColor) {
      button.style.setProperty("--rl-trigger-icon-color", String(iconColor));
    } else {
      button.style.removeProperty("--rl-trigger-icon-color");
    }

    const padding = normalizeCssSpacing(options.padding);
    if (padding) {
      button.style.setProperty("--rl-trigger-padding", padding);
    } else {
      button.style.removeProperty("--rl-trigger-padding");
    }
  }

  function buildToggle(action) {
    const button = createElement("button", {
      type: "button",
      className: "rl-control rl-toggle",
      "data-action": action,
    });
    button.textContent = getActionLabel(action);
    controlLabels.set(action, button);
    button.addEventListener("click", () => {
      handlers.onToggle(action);
    });
    return button;
  }

  function buildSpinner(action) {
    const container = createElement("div", {
      className: "rl-control",
      "data-action": action,
    });
    const label = createElement("div", {}, getActionLabel(action));
    const line = createElement("div", { className: "rl-spinner" });
    const minus = createElement(
      "button",
      { type: "button", "aria-label": getSpinnerAriaLabel("decrease", action) },
      "−",
    );
    const plus = createElement(
      "button",
      { type: "button", "aria-label": getSpinnerAriaLabel("increase", action) },
      "+",
    );
    const valueLabel = createElement("div", { className: "rl-spinner-value" }, "0");

    minus.addEventListener("click", () => handlers.onSpinner(action, -VALUE_STEP[action]));
    plus.addEventListener("click", () => handlers.onSpinner(action, VALUE_STEP[action]));

    line.appendChild(minus);
    line.appendChild(valueLabel);
    line.appendChild(plus);
    container.appendChild(label);
    container.appendChild(line);
    controlLabels.set(action, label);
    spinnerButtons.set(action, { minus, plus });

    return { container, valueLabel };
  }

  function open() {
    if (!info.root) {
      return;
    }
    info.open = true;
    info.root.classList.add("rl-open");
  }

  function close() {
    if (!info.root) {
      return;
    }
    info.open = false;
    info.root.classList.remove("rl-open");
  }

  function toggleOpen() {
    info.open ? close() : open();
  }

  function setToggle(action, active) {
    const element = controls.get(action);
    if (!element) {
      return;
    }
    element.classList.toggle("rl-active", Boolean(active));
    element.setAttribute("aria-pressed", active ? "true" : "false");
  }

  function setValue(action, value) {
    const element = spinners.get(action);
    if (!element) {
      return;
    }
    const sign = value > 0 ? "+" : "";
    const unit = VALUE_UNITS[action] ?? "";
    element.textContent = `${sign}${value}${unit}`;
  }

  function destroy() {
    info.customTriggerBindings.forEach((binding) => {
      binding.button.removeEventListener("click", binding.onClick);
      if (binding.generated && binding.button.parentElement) {
        binding.button.remove();
      }
    });
    info.customTriggerBindings = [];

    if (info.root) {
      info.root.remove();
    }
    info.open = false;
    info.root = null;
    info.trigger = null;
    info.overlay = null;
    info.panel = null;
    info.headerTitle = null;
    info.headerSubtitle = null;
    info.headerCloseButton = null;
    info.footerResetButton = null;
    info.footerCloseButton = null;
    info.languageButtons.clear();
    info.currentLanguage = null;
    sectionTitleNodes.length = 0;
    spinnerButtons.clear();
    controlLabels.clear();
    controls.clear();
    spinners.clear();
  }

  return {
    mount,
    open,
    close,
    toggleOpen,
    isOpen: () => info.open,
    getLanguage,
    setLanguage,
    setToggle,
    setValue,
    destroy,
  };
}

function normalizeTriggerVariant(value, fallback = "icon") {
  const raw = String(value || fallback).toLowerCase();
  if (raw === "icon" || raw === "text" || raw === "icon-text") {
    return raw;
  }
  if (raw === "text-icon") {
    return "icon-text";
  }
  return fallback;
}

function readLocaleValue(locale, path) {
  if (!locale || !path) {
    return undefined;
  }
  const segments = String(path).split(".");
  let cursor = locale;
  for (const segment of segments) {
    if (!cursor || typeof cursor !== "object") {
      return undefined;
    }
    cursor = cursor[segment];
  }
  return cursor;
}

function normalizeTriggerSize(value, fallback = "md") {
  const raw = String(value || fallback).toLowerCase();
  if (raw === "sm" || raw === "md" || raw === "lg") {
    return raw;
  }
  return fallback;
}

function normalizeIconSize(value, fallback = 22) {
  const numeric = Number(value);
  if (Number.isFinite(numeric) && numeric > 0) {
    return `${Math.round(numeric)}px`;
  }
  return `${Math.round(Number(fallback) || 22)}px`;
}

function normalizeCssLength(value, fallback = "0px") {
  if (value === undefined || value === null || value === "") {
    return fallback;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return `${value}px`;
  }
  const raw = String(value).trim();
  return raw || fallback;
}

function normalizeCssSpacing(value) {
  if (value === undefined || value === null || value === "") {
    return "";
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return `${value}px`;
  }
  return String(value).trim();
}

function escapeCssUrl(value) {
  return String(value).replace(/"/g, '\\"');
}

function positionClass(position) {
  switch (position) {
    case "bottom-left":
      return "rl-pos-bottom-left";
    case "top-left":
      return "rl-pos-top-left";
    case "top-right":
      return "rl-pos-top-right";
    default:
      return "rl-pos-bottom-right";
  }
}
